import { useRef, useEffect, useCallback } from "react";
import type { PhysicsState, ForceConfig } from "@/lib/physics";

interface PhysicsCanvasProps {
	state: PhysicsState;
	config: ForceConfig;
	netForce: number;
	frictionForce: number;
	surfaceColor?: string;
	showForceArrows?: boolean;
	showVelocityArrow?: boolean;
	label?: string;
	width?: number;
	height?: number;
}

export function PhysicsCanvas({
	state,
	config,
	netForce,
	frictionForce,
	surfaceColor = "#9ca3af",
	showForceArrows = true,
	showVelocityArrow = true,
	label,
	width = 700,
	height = 280,
}: PhysicsCanvasProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	const draw = useCallback(
		(ctx: CanvasRenderingContext2D) => {
			const dpr = window.devicePixelRatio || 1;
			const w = width;
			const h = height;

			ctx.clearRect(0, 0, w * dpr, h * dpr);
			ctx.save();
			ctx.scale(dpr, dpr);

			// Background
			ctx.fillStyle = "#0f172a";
			ctx.fillRect(0, 0, w, h);

			// Grid lines
			ctx.strokeStyle = "#1e293b";
			ctx.lineWidth = 1;
			for (let x = 0; x < w; x += 40) {
				ctx.beginPath();
				ctx.moveTo(x, 0);
				ctx.lineTo(x, h);
				ctx.stroke();
			}
			for (let y = 0; y < h; y += 40) {
				ctx.beginPath();
				ctx.moveTo(0, y);
				ctx.lineTo(w, y);
				ctx.stroke();
			}

			const groundY = h - 60;

			// Surface
			ctx.fillStyle = surfaceColor;
			ctx.fillRect(0, groundY, w, 8);

			// Surface texture lines
			ctx.strokeStyle = "rgba(0,0,0,0.2)";
			ctx.lineWidth = 1;
			for (let x = 0; x < w; x += 12) {
				ctx.beginPath();
				ctx.moveTo(x, groundY + 8);
				ctx.lineTo(x + 6, groundY + 20);
				ctx.stroke();
			}

			// Ground fill below surface
			ctx.fillStyle = "rgba(100,100,100,0.3)";
			ctx.fillRect(0, groundY + 8, w, h - groundY - 8);

			// Block dimensions scale with mass
			const blockBaseSize = 40;
			const massScale = Math.sqrt(config.mass / 5); // normalize around 5kg
			const blockW = blockBaseSize * Math.max(0.6, Math.min(1.8, massScale));
			const blockH = blockBaseSize * Math.max(0.6, Math.min(1.5, massScale));

			// Position: wrap within canvas bounds
			const pixelsPerMeter = 50;
			const centerX = w / 2;
			let blockX =
				centerX + ((state.position * pixelsPerMeter) % (w - 80)) - blockW / 2;
			// Keep block visible
			if (blockX < 10) blockX = 10;
			if (blockX > w - blockW - 10) blockX = w - blockW - 10;

			const blockY = groundY - blockH;

			// Block shadow
			ctx.fillStyle = "rgba(0,0,0,0.3)";
			ctx.fillRect(blockX + 3, blockY + 3, blockW, blockH);

			// Block body
			const gradient = ctx.createLinearGradient(blockX, blockY, blockX, blockY + blockH);
			gradient.addColorStop(0, "#3b82f6");
			gradient.addColorStop(1, "#1d4ed8");
			ctx.fillStyle = gradient;
			ctx.fillRect(blockX, blockY, blockW, blockH);

			// Block border
			ctx.strokeStyle = "#60a5fa";
			ctx.lineWidth = 2;
			ctx.strokeRect(blockX, blockY, blockW, blockH);

			// Mass label on block
			ctx.fillStyle = "#fff";
			ctx.font = "bold 13px system-ui, sans-serif";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillText(`${config.mass}kg`, blockX + blockW / 2, blockY + blockH / 2);

			// Force arrows
			const arrowY = blockY + blockH / 2;
			const arrowScale = 1.5;

			if (showForceArrows) {
				// Applied force arrow
				if (config.appliedForce !== 0) {
					const arrowLen = Math.min(
						Math.abs(config.appliedForce) * arrowScale,
						200,
					);
					const dir = Math.sign(config.appliedForce);
					const startX = dir > 0 ? blockX - 5 : blockX + blockW + 5;
					drawArrow(
						ctx,
						startX - dir * arrowLen,
						arrowY - 10,
						startX,
						arrowY - 10,
						"#22c55e",
						`F = ${Math.abs(config.appliedForce).toFixed(0)}N`,
					);
				}

				// Friction force arrow
				if (frictionForce !== 0) {
					const frictionLen = Math.min(
						Math.abs(frictionForce) * arrowScale,
						180,
					);
					const dir = -Math.sign(frictionForce);
					const startX = dir < 0 ? blockX - 5 : blockX + blockW + 5;
					drawArrow(
						ctx,
						startX,
						arrowY + 10,
						startX + dir * frictionLen,
						arrowY + 10,
						"#ef4444",
						`f = ${Math.abs(frictionForce).toFixed(1)}N`,
					);
				}

				// Net force indicator
				if (netForce !== 0) {
					const netLen = Math.min(Math.abs(netForce) * arrowScale, 180);
					const dir = Math.sign(netForce);
					const startX =
						dir > 0 ? blockX + blockW + 15 : blockX - 15;
					drawArrow(
						ctx,
						startX,
						blockY - 20,
						startX + dir * netLen,
						blockY - 20,
						"#f59e0b",
						`Fnet = ${Math.abs(netForce).toFixed(1)}N`,
					);
				}
			}

			// Velocity arrow
			if (showVelocityArrow && Math.abs(state.velocity) > 0.1) {
				const velLen = Math.min(Math.abs(state.velocity) * 15, 150);
				const dir = Math.sign(state.velocity);
				drawArrow(
					ctx,
					blockX + blockW / 2,
					blockY - 40,
					blockX + blockW / 2 + dir * velLen,
					blockY - 40,
					"#a78bfa",
					`v = ${Math.abs(state.velocity).toFixed(1)} m/s`,
				);
			}

			// Info panel
			ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
			ctx.fillRect(8, 8, 180, label ? 80 : 65);
			ctx.strokeStyle = "#334155";
			ctx.lineWidth = 1;
			ctx.strokeRect(8, 8, 180, label ? 80 : 65);

			ctx.fillStyle = "#94a3b8";
			ctx.font = "12px system-ui, sans-serif";
			ctx.textAlign = "left";
			ctx.textBaseline = "top";

			let infoY = 14;
			if (label) {
				ctx.fillStyle = "#e2e8f0";
				ctx.font = "bold 12px system-ui, sans-serif";
				ctx.fillText(label, 16, infoY);
				infoY += 18;
				ctx.fillStyle = "#94a3b8";
				ctx.font = "12px system-ui, sans-serif";
			}
			ctx.fillText(`Position: ${state.position.toFixed(2)} m`, 16, infoY);
			ctx.fillText(
				`Velocity: ${state.velocity.toFixed(2)} m/s`,
				16,
				infoY + 16,
			);
			ctx.fillText(
				`Acceleration: ${state.acceleration.toFixed(2)} m/s²`,
				16,
				infoY + 32,
			);

			ctx.restore();
		},
		[
			state,
			config,
			netForce,
			frictionForce,
			surfaceColor,
			showForceArrows,
			showVelocityArrow,
			label,
			width,
			height,
		],
	);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const dpr = window.devicePixelRatio || 1;
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		draw(ctx);
	}, [draw, width, height]);

	return (
		<canvas
			ref={canvasRef}
			style={{ width, height }}
			className="rounded-lg border border-border w-full max-w-full"
		/>
	);
}

function drawArrow(
	ctx: CanvasRenderingContext2D,
	fromX: number,
	fromY: number,
	toX: number,
	toY: number,
	color: string,
	label?: string,
) {
	const headLen = 10;
	const angle = Math.atan2(toY - fromY, toX - fromX);

	ctx.strokeStyle = color;
	ctx.fillStyle = color;
	ctx.lineWidth = 3;
	ctx.lineCap = "round";

	// Line
	ctx.beginPath();
	ctx.moveTo(fromX, fromY);
	ctx.lineTo(toX, toY);
	ctx.stroke();

	// Arrowhead
	ctx.beginPath();
	ctx.moveTo(toX, toY);
	ctx.lineTo(
		toX - headLen * Math.cos(angle - Math.PI / 6),
		toY - headLen * Math.sin(angle - Math.PI / 6),
	);
	ctx.lineTo(
		toX - headLen * Math.cos(angle + Math.PI / 6),
		toY - headLen * Math.sin(angle + Math.PI / 6),
	);
	ctx.closePath();
	ctx.fill();

	// Label
	if (label) {
		ctx.font = "bold 11px system-ui, sans-serif";
		ctx.textAlign = "center";
		ctx.textBaseline = "bottom";
		ctx.fillText(label, (fromX + toX) / 2, Math.min(fromY, toY) - 5);
	}
}
